import { CommonModule } from '@angular/common';
import {
  Component, EventEmitter, HostListener, Input, OnChanges,
  Output, SimpleChanges, ViewChild, ElementRef, AfterViewInit
} from '@angular/core';

@Component({
  standalone: true,
  selector: 'app-emergency-stop-button',
  imports: [CommonModule],
  templateUrl: './emergency-stop-button.component.html',
  styleUrls: ['./emergency-stop-button.component.scss'],
})
export class EmergencyStopButtonComponent implements OnChanges, AfterViewInit {
  /** Durchmesser in px */
  @Input() size = 160;
  /** Press-and-hold in ms. 0 = sofort */
  @Input() holdDurationMs = 900;
  /** Normaler Text */
  @Input() label = 'NOT-AUS';
  /** Text nach Aktivierung */
  @Input() activatedLabel = 'AUS';
  /** Nach Auslösung sperren */
  @Input() lockAfterTrigger = true;
  /** Zeigt die (innere) Fortschrittsleiste beim Halten */
  @Input() showProgressBar = true;
  /** RBAC: sichtbar, aber gesperrt (div[role=button] — kein natives disabled) */
  @Input() disabled = false;

  @Output() stop = new EventEmitter<void>();

  activated = false;
  pressing = false;
  progress = 0; // 0..1
  fontSizePx = 16;

  private pressStartTs = 0;
  private rafId: number | null = null;

  @ViewChild('btnEl') btnEl!: ElementRef<HTMLElement>;
  @ViewChild('textEl') textEl!: ElementRef<HTMLElement>;

  ngAfterViewInit() { this.fitTextSoon(); }
  ngOnChanges(_: SimpleChanges) { this.fitTextSoon(); }

  @HostListener('window:resize')
  onResize() { this.fitTextSoon(); }

  private fitTextSoon() {
    // Warten bis Template/Bindings aktualisiert sind
    queueMicrotask(() => this.fitText());
  }

  private fitText() {
    const btn = this.btnEl?.nativeElement;
    const text = this.textEl?.nativeElement;
    if (!btn || !text) return;

    // verfügbarer Innenraum = size - 2*pad
    const pad = this.size * 0.18; // muss zum SCSS (--pad) passen
    const availW = this.size - 2 * pad;
    const availH = this.size - 2 * pad;

    // einzeilig ohne Umbruch
    text.style.whiteSpace = 'nowrap';

    let lo = 10;
    let hi = Math.floor(this.size * 0.24); // obere Schranke
    let best = lo;

    // einfache Suche von klein -> groß
    for (let f = lo; f <= hi; f++) {
      text.style.fontSize = f + 'px';
      // messen
      const w = text.scrollWidth;
      const h = text.scrollHeight;
      if (w <= availW && h <= availH) {
        best = f;
      } else {
        break;
      }
    }
    this.fontSizePx = best;
    text.style.fontSize = best + 'px';
  }

  onPressStart(ev?: Event) {
    if (this.disabled) return;
    if (this.activated && this.lockAfterTrigger) return;
    ev?.preventDefault();

    if (this.holdDurationMs <= 0) {
      this.trigger();
      return;
    }
    this.pressing = true;
    this.progress = 0;
    this.pressStartTs = performance.now();

    const tick = (now: number) => {
      const elapsed = now - this.pressStartTs;
      this.progress = Math.min(1, elapsed / this.holdDurationMs);
      if (this.progress >= 1) {
        this.trigger();
        this.onPressEnd();
        return;
      }
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  onPressEnd() {
    if (!this.pressing) return;
    this.pressing = false;
    this.progress = 0;
    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  onKeyDown(e: KeyboardEvent) {
    if (this.disabled || e.repeat) return;
    if (e.key === ' ' || e.key === 'Enter') this.onPressStart(e);
  }
  onKeyUp(e: KeyboardEvent) {
    if (e.key === ' ' || e.key === 'Enter') this.onPressEnd();
  }

  private trigger() {
    if (this.activated && this.lockAfterTrigger) return;
    this.stop.emit();
    if (this.lockAfterTrigger) {
      this.activated = true;
      this.fitTextSoon(); // Text ändert sich -> neu einpassen
    }
  }
}
